/**
 * briefGroups — the three groups on the Decisions list.
 *
 *   Needs you          — the next action is yours
 *   Waiting on others  — proposals are outstanding, or someone else decides
 *   Decided            — on the record (decided or archived)
 *
 * Derived from rows and the viewer, not from a filter panel. Within a group,
 * the soonest deadline comes first; undated briefs follow, newest first.
 */

import type { Brief } from '../../../types/brief';

export type BriefGroupId = 'needs_you' | 'waiting' | 'decided';

export interface BriefListRow {
  brief: Brief;
  /** Named options and how many have a proposal — the list query supplies these. */
  optionCount: number;
  submittedCount: number;
  criteriaCount: number;
  /** True when the viewer is the person the ball is with. */
  needsViewer: boolean;
}

export interface BriefGroups {
  needs_you: BriefListRow[];
  waiting: BriefListRow[];
  decided: BriefListRow[];
}

export const BRIEF_GROUP_LABELS: Record<BriefGroupId, string> = {
  needs_you: 'Needs you',
  waiting: 'Waiting on others',
  decided: 'Decided',
};

function bySoonestThenNewest(a: BriefListRow, b: BriefListRow): number {
  const ad = a.brief.deadline;
  const bd = b.brief.deadline;
  if (ad && bd && ad !== bd) return ad < bd ? -1 : 1;
  if (ad && !bd) return -1;
  if (!ad && bd) return 1;
  return a.brief.updated_at < b.brief.updated_at ? 1 : a.brief.updated_at > b.brief.updated_at ? -1 : 0;
}

export function groupBriefs(rows: BriefListRow[]): BriefGroups {
  const groups: BriefGroups = { needs_you: [], waiting: [], decided: [] };
  for (const row of rows) {
    const s = row.brief.status;
    if (s === 'decided' || s === 'archived') groups.decided.push(row);
    // Deferred: waiting on a time or an event, not on the viewer.
    else if (s === 'deferred') groups.waiting.push(row);
    else if (row.needsViewer) groups.needs_you.push(row);
    else groups.waiting.push(row);
  }
  groups.needs_you.sort(bySoonestThenNewest);
  groups.waiting.sort(bySoonestThenNewest);
  groups.decided.sort((a, b) => (a.brief.updated_at < b.brief.updated_at ? 1 : -1));
  return groups;
}

/** Plain-text search over the fields a person would remember. */
export function matchesSearch(brief: Brief, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [brief.title, brief.context, brief.decision_authority, brief.scope_label, ...(brief.tags ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}
